import {LinkedListNode} from '../../utils/grid-widget/linked-list-node';
import {Observable} from 'rxjs';
import {gridWidgetLogger} from '../../utils/grid-widget/grid-widget-logger';
import {RestArgument} from './rest-argument';
import {Persistable} from './persistable';
/**
 * Something that GridWidgetTask is a task that the grid widget will run during it's execution chain.
 */
export abstract class GridWidgetTask implements LinkedListNode, Persistable {
  /**
   * The results of the last run will be stored here.
   */
  result: Array<any>;

  /**
   * The name of the task. Something like 'Filter', 'Paginate', or 'Sort'
   */
  name: string;

  /**
   * When this task is off, the task will be skipped in the filter chain.
   */
  gridWidgetTaskActive = true;

  nextNode: GridWidgetTask;

  previousNode: GridWidgetTask;

  logColor = '#000000';

  static get DATA_TASK_PRIORITY(): number {
    return 0;
  }

  static get BEFORE_FILTER_TASK_PRIORITY(): number {
    return 500;
  }

  static get FILTER_TASK_PRIORITY(): number {
    return 1000;
  }

  static get AFTER_FILTER_TASK_PRIORITY(): number {
    return 1500;
  }

  static get RESET_PAGINATION_TASK_PRIORITY(): number {
    return 1750;
  }

  static get SORT_TASK_PRIORITY(): number {
    return 2000;
  }

  static get AFTER_SORT_TASK_PRIORITY(): number {
    return 2500;
  }

  static get PAGINATE_TASK_PRIORITY(): number {
    return 3000;
  }

  static get AFTER_PAGINATE_TASK_PRIORITY(): number {
    return 3500;
  }

  static get COMPLETE_TASK_PRIORITY(): number {
    return 4000;
  }

  //These should be set during preRunSetup if the task is turned off.
  //As they are for serverside mode.
  restArguments: Array<RestArgument>;

  abstract getPriority(): number;

  /**
   * The main function to exectute.
   */
  abstract run(data: Array<any>): Array<any> | Observable<Array<any>>;

  isGridWidgetTaskActive(): boolean {
    return this.gridWidgetTaskActive;
  }

  setGridWidgetTaskActive(value): void {
    this.gridWidgetTaskActive = value;
  }

  /**
   * A function that the execute task will run prior to calling run.
   * This will be called regardless of whether the task is active.
   * This gets run each time the task chain executes.
   */
  preRunSetup(): void {
  }

  /**
   * This is a live cycle hook that will be called when one or more of the task chain prerequisites fail.
   */
  taskChainCanceled(): void {
  }

  /**
   * Kicks off the task chain starting with this task.
   * An Asynchronous function.
   */
  execute(): void {
    let logResult = (result: Array<any>, isSynchronous) => {
      let logString = '* * * ' + (isSynchronous ? '' : 'ASYNCHRONOUS ') + 'task complete: "' + this.name + '"';
      if (result == null) {
        logString += ' result: null';
      } else {
        logString += ' result.length: ' + result.length;
      }
      logString += ' * * *';

      gridWidgetLogger.log(logString, this.logColor);
    };

    //Runs whether the task is active or not.
    this.preRunSetup();

    if (this.isGridWidgetTaskActive()) {
      let result: Array<any> | Observable<Array<any>> = this.run(this.previousNode.result);
      if (result == null) {
        result = [];
      }

      if (result instanceof Array) {
        this.result = <Array<any>>result;
        logResult(this.result, true);
        this.executeNext();
      } else {
        //Must be an observable. Subscribe to it.
        let runSubscription = (< Observable<Array<any>> >result).subscribe((response: Array<any>) => {
          //In case we get the same observable object every time, we unsubscribe so there
          //won't be a build up of subscriptions.  However, the observalbe coming back
          //will likely be from an Angular 2 REST call so we would probably get a new one every time.
          runSubscription.unsubscribe();

          this.result = response;
          logResult(this.result, false);
          this.executeNext();
        }, (error) => {
          console.error('An error occurred while running ' + this.name);
          console.error(error);
          this.result = [];
          this.executeNext();
        });
      }
    } else {
      this.result = this.previousNode.result;
      this.executeNext();
    }
  }

  /**
   * You MUST be able to call JSON.stringify on the returned object.
   */
  getPersistenceObject(): Object | undefined {
    return;
  }

  restoreFromPersistenceObject(value: Object): void { }

  private executeNext(): void {
    if (this.nextNode != null) {
      this.nextNode.execute();
    }
  }
}
